import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { API_URL, QUERY_KEYS } from "../constants"

export interface Condition {
  _id: string
  name: string
  recommendations: { message: string }[]
  organizationId: string
  creatorId: string
  createdAt: string
  updatedAt: string
}

export function useConditions(organizationId: string | undefined, token: string) {
  return useQuery<Condition[]>({
    queryKey: QUERY_KEYS.conditions(organizationId ?? ""),
    queryFn: async () => {
      const url = `${API_URL}/organizations/${organizationId}/conditions`
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) throw new Error("Failed to fetch conditions")
      return res.json()
    },
    enabled: !!organizationId,
  })
}

export function useCreateCondition(token: string) {
  const queryClient = useQueryClient()
  return useMutation<Condition, unknown, Omit<Condition, "_id" | "creatorId" | "createdAt" | "updatedAt">>({
    mutationFn: async (payload) => {
      const res = await fetch(`${API_URL}/organizations/${payload.organizationId}/conditions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error("Failed to create condition")
      return res.json()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.conditions(variables.organizationId),
      })
    },
  })
}

export function useUpdateCondition(organizationId: string, token: string) {
  const queryClient = useQueryClient()
  return useMutation<
    Condition,
    unknown,
    { id: string } & Partial<Omit<Condition, "_id" | "creatorId" | "createdAt" | "updatedAt">>
  >({
    mutationFn: async ({ id, ...payload }) => {
      const res = await fetch(`${API_URL}/organizations/${organizationId}/conditions/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error("Failed to update condition")
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditions(organizationId) })
    },
  })
}

export function useDeleteCondition(organizationId: string, token: string) {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/organizations/${organizationId}/conditions/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) throw new Error("Failed to delete condition")
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditions(organizationId) })
    },
  })
}
